/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import HelpIcon from '@mui/icons-material/Help';
import {
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material';

import { Languages } from 'shared';

import { ArrowRoundedIcon } from '../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../elements/HUTextInput/utils';
import { OutlinedPasswordInput } from '../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../i18n';
import { useFormStyles } from '../../../styles/form';
import { ObjectMap } from '../../../types/utils/ObjectMap';
import {
  ApiValidationGetFieldError,
  ApiValidationHasFieldError,
  ApiValidationResetFieldError,
} from '../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../utils/useValidationTranslation';
import { useSystemConfigurationFormStyles } from '../../SystemConfigurationPage/components/SystemConfigurationForm/SystemConfigurationForm.styles';
import { EmailType } from '../../SystemConfigurationPage/SystemConfigurationPage.consts';
import { SystemConfigurationFormValues } from '../../SystemConfigurationPage/SystemConfigurationPage.types';

export type EmailConfigurationFormProps = FormikProps<SystemConfigurationFormValues> & {
  isFetching: boolean;
  hasFieldApiError: ApiValidationHasFieldError<ObjectMap>;
  getFieldApiError: ApiValidationGetFieldError<ObjectMap>;
  resetFieldApiError: ApiValidationResetFieldError<ObjectMap>;
};

export const EmailConfigurationForm = memo<EmailConfigurationFormProps>(props => {
  const classes = useSystemConfigurationFormStyles();
  const formClasses = useFormStyles();
  const { vtr } = useValidationTranslation();
  const { t, i18n } = useTranslation(Namespaces.Admin);

  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    hasFieldApiError,
    getFieldApiError,
    resetFieldApiError,
    setFieldTouched,
    setFieldValue,
    isFetching,
  } = props;

  const hasError = useCallback(
    (name: keyof SystemConfigurationFormValues): boolean => {
      return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
    },
    [errors, hasFieldApiError, touched],
  );

  const getError = useCallback(
    (name: keyof SystemConfigurationFormValues): string => {
      const error = errors[name] || getFieldApiError(name);
      return error || '';
    },
    [errors, getFieldApiError],
  );

  const googleHelpUrl = useMemo(
    () => `https://support.google.com/accounts/answer/185833?hl=${encodeURIComponent(i18n.language)}`,
    [i18n.language],
  );

  const renderAutocompleteInput = useCallback(
    (renderParams: AutocompleteRenderInputParams) => (
      <>
        <InputLabel htmlFor="type">{t('email_configuration.form.type')}</InputLabel>
        <OutlinedInput
          {...destructRenderInputParams(renderParams)}
          onBlur={handleBlur}
          name="type"
          label={t('email_configuration.form.type')}
          placeholder={t('email_configuration.form.type')}
          disabled={isFetching}
          readOnly
        />
      </>
    ),
    [handleBlur, isFetching, t],
  );

  const shouldUseGmail = !!values.emailAddress && !!values.emailToken;

  const handleChangeForm = useCallback(
    async (_e: unknown, type: EmailType) => {
      await setFieldValue('type', type);

      await Promise.all([
        setFieldValue('smtpHost', null),
        setFieldValue('smtpPort', null),
        setFieldValue('smtpSecure', null),
        setFieldValue('smtpUser', null),
        setFieldValue('smtpPass', null),
        setFieldValue('smtpFromEmail', null),
        setFieldValue('emailAddress', null),
        setFieldValue('emailToken', null),
      ]);
    },
    [setFieldValue],
  );

  const handleEnquiryEmailLanguageChange = useCallback(
    async (_e: unknown, value: string | null) => {
      if (value) {
        await Promise.all([
          setFieldValue('enquiryEmailLanguage', value, true),
          setFieldTouched('enquiryEmailLanguage', true),
        ]);
        resetFieldApiError('enquiryEmailLanguage');
      }
    },
    [resetFieldApiError, setFieldTouched, setFieldValue],
  );

  const getPasswordVisibilityLabel = useCallback(
    (name: 'smtp' | 'gmail', type: 'show' | 'hide') =>
      t(`email_configuration.${name === 'smtp' ? 'password' : 'token'}.${type}`),
    [t],
  );

  const smtpForm = useMemo(
    () => (
      <>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('smtpHost')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpHost">{t('email_configuration.form.hostname')}</InputLabel>
          <OutlinedInput
            name="smtpHost"
            label={t('email_configuration.form.hostname')}
            value={values.smtpHost}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('smtpHost') && (
            <FormHelperText>
              {vtr(getError('smtpHost'), {
                field: t('email_configuration.form.hostname'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('smtpPort')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpPort">{t('email_configuration.form.port')}</InputLabel>
          <OutlinedInput
            name="smtpPort"
            label={t('email_configuration.form.port')}
            value={values.smtpPort}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('smtpPort') && (
            <FormHelperText>
              {vtr(getError('smtpPort'), {
                field: t('email_configuration.form.port'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('smtpUser')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpUser">{t('email_configuration.form.username')}</InputLabel>
          <OutlinedInput
            name="smtpUser"
            label={t('email_configuration.form.username')}
            value={values.smtpUser}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('smtpUser') && (
            <FormHelperText>
              {vtr(getError('smtpUser'), {
                field: t('email_configuration.form.username'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('smtpPass')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpPass">{t('email_configuration.form.password')}</InputLabel>
          <OutlinedPasswordInput
            name="smtpPass"
            label={t('email_configuration.form.password')}
            value={values.smtpPass}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            showTooltip={getPasswordVisibilityLabel('smtp', 'show')}
            hideTooltip={getPasswordVisibilityLabel('smtp', 'hide')}
          />
          {hasError('smtpPass') && (
            <FormHelperText>
              {vtr(getError('smtpPass'), {
                field: t('email_configuration.form.password'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <div className={classes.checkboxes}>
          <FormControlLabel
            name="smtpSecure"
            checked={values.smtpSecure}
            onChange={handleChange}
            control={<Checkbox color="primary" />}
            label={t('email_configuration.form.secure')}
            labelPlacement="end"
            disabled={isFetching || !values.useSmtp}
          />
        </div>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('smtpFromEmail')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpFromEmail">{t('email_configuration.form.sender_email')}</InputLabel>
          <OutlinedInput
            name="smtpFromEmail"
            label={t('email_configuration.form.sender_email')}
            value={values.smtpFromEmail}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('smtpFromEmail') && (
            <FormHelperText>
              {vtr(getError('smtpFromEmail'), {
                field: t('email_configuration.form.sender_email'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </>
    ),
    [
      classes.checkboxes,
      formClasses.formControl,
      formClasses.widthMedium,
      getError,
      getPasswordVisibilityLabel,
      handleBlur,
      handleChange,
      hasError,
      isFetching,
      t,
      values.smtpFromEmail,
      values.smtpHost,
      values.smtpPass,
      values.smtpPort,
      values.smtpSecure,
      values.smtpUser,
      values.useSmtp,
      vtr,
    ],
  );

  const gmailForm = useMemo(
    () => (
      <>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('emailAddress')}
          variant="outlined"
        >
          <InputLabel htmlFor="emailAddress">{t('email_configuration.form.email_address')}</InputLabel>
          <OutlinedInput
            name="emailAddress"
            label={t('email_configuration.form.email_address')}
            type="email"
            value={values.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('emailAddress') && (
            <FormHelperText>
              {vtr(getError('emailAddress'), {
                field: t('email_configuration.form.email_address'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('emailToken')}
          variant="outlined"
        >
          <InputLabel htmlFor="emailToken">{t('email_configuration.form.email_token')}</InputLabel>
          <Box display="flex" flexDirection="row" alignItems="center" gap={16}>
            <OutlinedPasswordInput
              name="emailToken"
              label={t('email_configuration.form.email_token')}
              value={values.emailToken}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isFetching}
              fullWidth
              showTooltip={getPasswordVisibilityLabel('gmail', 'show')}
              hideTooltip={getPasswordVisibilityLabel('gmail', 'hide')}
            />
            <Tooltip title={t('email_configuration.token.tooltip')}>
              <IconButton href={googleHelpUrl} target="_blank" size="large">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {hasError('emailToken') && (
            <FormHelperText>
              {vtr(getError('emailToken'), {
                field: t('email_configuration.form.email_token'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </>
    ),
    [
      formClasses.formControl,
      formClasses.widthMedium,
      getError,
      getPasswordVisibilityLabel,
      googleHelpUrl,
      handleBlur,
      handleChange,
      hasError,
      isFetching,
      t,
      values.emailAddress,
      values.emailToken,
      vtr,
    ],
  );

  useEffect(() => {
    void setFieldValue('useSmtp', !shouldUseGmail);
  }, [setFieldValue, shouldUseGmail]);

  useEffect(() => {
    void setFieldValue('type', shouldUseGmail ? EmailType.GMAIL : EmailType.SMTP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={classes.groupContainer}>
        <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} variant="outlined">
          <HUAutocomplete
            renderInput={renderAutocompleteInput}
            options={Object.values(EmailType)}
            onChange={handleChangeForm}
            value={values.type}
            disableClearable
          />
        </FormControl>
        {values.type === EmailType.SMTP ? smtpForm : gmailForm}
      </div>
      <div className={classes.groupContainer}>
        <Typography variant="h4">{t('email_configuration.group_titles.system_notifications')}</Typography>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('enquiryEmails')}
          variant="outlined"
        >
          <InputLabel htmlFor="enquiryEmails">{t('email_configuration.form.enquiryEmails')}</InputLabel>
          <OutlinedInput
            name="enquiryEmails"
            label={t('email_configuration.form.enquiryEmails')}
            value={values.enquiryEmails}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('enquiryEmails') && (
            <FormHelperText>
              {vtr(getError('enquiryEmails'), {
                field: t('email_configuration.form.enquiryEmails'),
              })}
            </FormHelperText>
          )}
          <FormHelperText error={false}>{t('email_configuration.form.email_hint')}</FormHelperText>
        </FormControl>
        <FormControl className={classes.languageInput} error={hasError('enquiryEmailLanguage')} variant="outlined">
          <HUAutocomplete
            popupIcon={<ArrowRoundedIcon />}
            renderInput={renderParams => (
              <>
                <InputLabel htmlFor="enquiryEmailLanguage">
                  {t('email_configuration.form.notification_language')}
                </InputLabel>
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  label={t('email_configuration.form.notification_language')}
                  name="enquiryEmailLanguage"
                  placeholder={t('email_configuration.form.notification_language')}
                />
              </>
            )}
            id="enquiryEmailLanguage"
            options={Languages}
            disableClearable={true}
            openOnFocus={true}
            disabled={isFetching}
            value={values.enquiryEmailLanguage}
            onChange={handleEnquiryEmailLanguageChange}
            getOptionLabel={v => t(`${Namespaces.UserProfile}:profile.language.${v}`)}
          />
          {hasError('enquiryEmailLanguage') && <FormHelperText>{vtr(getError('enquiryEmailLanguage'))}</FormHelperText>}
        </FormControl>
      </div>
    </>
  );
});
